import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  LayoutDashboard,
  ShoppingBag,
  Users,
  Bike,
  Store,
  Tags,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  ChevronLeft,
  ChevronRight,
  Bell,
} from 'lucide-react';
import { useSettingsStore } from '@/stores/settingsStore';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from '@/hooks/useTranslation';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, labelKey: 'dashboard' as const },
  { path: '/dashboard/orders', icon: ShoppingBag, labelKey: 'orders' as const },
  { path: '/dashboard/users', icon: Users, labelKey: 'users' as const },
  { path: '/dashboard/drivers', icon: Bike, labelKey: 'drivers' as const },
  { path: '/dashboard/cafes', icon: Store, labelKey: 'cafes' as const },
  { path: '/dashboard/categories', icon: Tags, labelKey: 'categories' as const },
  { path: '/dashboard/reports', icon: BarChart3, labelKey: 'reports' as const },
  { path: '/dashboard/settings', icon: Settings, labelKey: 'settings' as const },
];

export default function AdminLayout() {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const { theme, toggleTheme, toggleLanguage, sidebarCollapsed, toggleSidebar } = useSettingsStore();
  const { logout, user } = useAuthStore();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isRtl = language === 'ar';
  const CollapseIcon = isRtl ? (sidebarCollapsed ? ChevronLeft : ChevronRight) : (sidebarCollapsed ? ChevronRight : ChevronLeft);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 ${isRtl ? 'right-0' : 'left-0'} h-full z-50 
          bg-gradient-to-b from-primary-600 to-primary-800 
          dark:from-slate-800 dark:to-slate-900
          transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'w-20' : 'w-64'}
          ${mobileMenuOpen ? 'translate-x-0' : isRtl ? 'translate-x-full lg:translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          {!sidebarCollapsed && (
            <span className="text-xl font-bold text-white">🚀 أليستا</span>
          )}
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-white/10 text-white hidden lg:block"
          >
            <CollapseIcon size={20} />
          </button>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 rounded-lg hover:bg-white/10 text-white lg:hidden"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-xl transition-all
                ${isActive
                  ? 'bg-white text-primary-600 font-semibold shadow-lg'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
                }
                ${sidebarCollapsed ? 'justify-center' : ''}`
              }
            >
              <item.icon size={22} />
              {!sidebarCollapsed && <span>{t(item.labelKey)}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-4 left-0 right-0 px-3">
          <button
            onClick={handleLogout}
            className={`
              flex items-center gap-3 px-3 py-3 rounded-xl w-full
              text-white/80 hover:bg-red-500/20 hover:text-red-300 transition-all
              ${sidebarCollapsed ? 'justify-center' : ''}
            `}
          >
            <LogOut size={22} />
            {!sidebarCollapsed && <span>{t('logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`
          transition-all duration-300
          ${sidebarCollapsed ? (isRtl ? 'lg:mr-20' : 'lg:ml-20') : (isRtl ? 'lg:mr-64' : 'lg:ml-64')}
        `}
      >
        {/* Topbar */}
        <header className="h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-30">
          <div className="h-full px-4 flex items-center justify-between">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 lg:hidden"
            >
              <Menu size={24} className="text-slate-600 dark:text-slate-300" />
            </button>

            {/* Right side */}
            <div className="flex items-center gap-2 ms-auto">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-700 
                         text-sm font-semibold text-slate-600 dark:text-slate-300
                         hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                {language === 'ar' ? 'EN' : 'عربي'}
              </button>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 
                         text-slate-600 dark:text-slate-300
                         hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
              </button>

              {/* Notifications */}
              <button className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 
                               text-slate-600 dark:text-slate-300 relative
                               hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
                <Bell size={20} />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full 
                               text-xs text-white flex items-center justify-center">
                  3
                </span>
              </button>

              {/* User */}
              <div className="flex items-center gap-3 ps-3 border-s border-slate-200 dark:border-slate-600">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 
                              flex items-center justify-center text-white font-semibold">
                  {user?.name?.[0] || 'م'}
                </div>
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 hidden sm:block">
                  {user?.name || 'المدير'}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
